import {CollaborationCreateOrUpdate} from 'auth/PermissionConstants';
import {
  AgentApprovalCategory,
  RoutineRunAs,
  RoutineStatus,
  TriggerType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import type {HasAvatar} from 'ui-library/avatar/Avatar';

import {RoutinesQuery_routines_RoutineNode_messageBlocks as GqlMessageBlockGenerated} from './__generated/RoutinesQuery';
import {TriggerKind} from './triggerConstants';

export {AgentApprovalCategory, RoutineRunAs, RoutineStatus, TriggerType};

export type GqlMessageBlock = GqlMessageBlockGenerated;

export type RoutineCreator = HasAvatar & {id: string};

export interface TextMessageBlock {
  type: 'text';
  text: string;
}

interface BaseFileMessageBlock {
  type: 'file';
  filename: string;
  media_type: string;
}

// A file block carries exactly one of:
// - external_file_id: an already-persisted attachment (loaded from a prior save).
// - s3_location: a freshly uploaded, not-yet-finalized temporary upload. The backend
//   finalizes it into a permanent attachment when the routine is saved.
// Modeled as a discriminated union so illegal states (both or neither) are unrepresentable.
export type FileMessageBlock = BaseFileMessageBlock &
  (
    | {external_file_id: string; s3_location?: never; source_routine_id?: string}
    | {s3_location: string; external_file_id?: never; source_routine_id?: never}
  );

export type MessageBlock = TextMessageBlock | FileMessageBlock;

/** Tag persisted file attachments for cross-routine copy; fresh `s3_location` uploads are unchanged. */
export function tagFileBlocksWithCopySource(
  fileBlocks: Array<FileMessageBlock>,
  sourceRoutineId: string
): Array<FileMessageBlock> {
  return fileBlocks.map((block) => {
    if (!('external_file_id' in block) || block.external_file_id == null) {
      return block;
    }
    return {
      ...block,
      source_routine_id: sourceRoutineId,
    };
  });
}

export function gqlBlocksToMessageBlocks(blocks: Array<GqlMessageBlock>): Array<MessageBlock> {
  return blocks.map((block) => {
    if (block.__typename === 'DisplayTextBlockNode') {
      return {type: 'text' as const, text: block.text};
    }
    return {
      type: 'file' as const,
      external_file_id: block.externalFileId,
      filename: block.filename,
      media_type: block.mediaType,
    };
  });
}

// NOTE: trigger_config is stored as raw JSONB and passed through the GraphQL resolver as-is.
// Keys are snake_case in the DB. The creation flow will finalize the config schema.
export interface TimeTriggerConfig {
  timezone: string;
  run_time: string;
  freq_type: 'daily' | 'weekly' | 'monthly';
  recurrence_interval: number;
  weekly_days?: Array<number>;
}

// Which objects a routine may act on, relative to its execution identity. Mirrors the backend
// TriggerObjectScope enum values. An omitted object_scopes defaults to "viewable" on the backend
// (current behavior).
export type TriggerObjectScope = 'authored' | 'viewable';

export interface EventTriggerConfig {
  object_type: string;
  trigger_kind?: TriggerKind;
  field?: string;
  object_ids?: Array<string>;
  to_values?: Array<string>;
  schema_id?: string;
  object_scopes?: Array<TriggerObjectScope>;
}

// Returns a copy of the trigger config with object_scopes in a stable (alphabetical) order — a
// no-op for configs without them, e.g. time-based. The backend union is order-agnostic and the
// order is never shown to users (the form renders a fixed layout), so any deterministic order works;
// this just makes a reordered-but-equal selection compare equal in the dirty check and persist
// consistently.
export function withCanonicalObjectScopes(
  config: Partial<TimeTriggerConfig> | Partial<EventTriggerConfig>
): Partial<TimeTriggerConfig> | Partial<EventTriggerConfig> {
  const scopes = (config as Partial<EventTriggerConfig>).object_scopes;
  if (scopes == null) {
    return config;
  }
  return {
    ...(config as Partial<EventTriggerConfig>),
    object_scopes: [...scopes].sort((a, b) => a.localeCompare(b)),
  };
}

export interface Routine {
  id: string;
  name: string | null;
  description: string | null;
  prompt: string;
  agentType: string;
  messageBlocks: Array<MessageBlock>;
  triggerType: TriggerType;
  triggerConfig: TimeTriggerConfig | EventTriggerConfig;
  status: RoutineStatus;
  allowedApprovalCategories: Array<AgentApprovalCategory>;
  agentWorkspaceFolderId: string | null;
  runAs: RoutineRunAs;
  memoryEnabled: boolean;
  createdAt: string;
  modifiedAt: string;
  nextRunAt: string | null;
  archived?: boolean;
  permissions: {
    appendable: boolean;
    archivable?: boolean;
    writable: boolean;
  };
  creatorId: string;
  creator: RoutineCreator | null;
}

export interface AllowedTriggerField {
  field: string;
  displayName: string;
  allowedValues: Array<string>;
}

export interface AllowedEventTrigger {
  objectType: string;
  supportsCreation: boolean;
  schemaSearchType: string | null;
  fields: Array<AllowedTriggerField>;
}

export interface UpdateRoutinePayload {
  name?: string;
  description?: string;
  messageBlocks?: Array<MessageBlock>;
  triggerConfig?: TimeTriggerConfig | EventTriggerConfig;
  allowedApprovalCategories?: Array<AgentApprovalCategory>;
  agentWorkspaceFolderId?: string | null;
  memoryEnabled?: boolean;
  llmFeatureAccess?: Array<LLMFeatureAccessUpdate>;
}

export interface LLMFeatureAccessUpdate {
  configKey: string;
  enabled: boolean;
}

export interface CreateRoutinePayload {
  name: string;
  description?: string;
  messageBlocks: Array<MessageBlock>;
  agentType: string;
  triggerType: TriggerType;
  triggerConfig: TimeTriggerConfig | EventTriggerConfig;
  allowedApprovalCategories: Array<AgentApprovalCategory>;
  agentWorkspaceFolderId: string | null;
  runAs: RoutineRunAs;
  memoryEnabled: boolean;
  collaborations?: Array<CollaborationCreateOrUpdate>;
  llmFeatureAccess?: Array<LLMFeatureAccessUpdate>;
}

export function extractPromptFromBlocks(blocks: Array<MessageBlock>): string {
  const textBlock = blocks.find((b): b is TextMessageBlock => b.type === 'text');
  return textBlock?.text ?? '';
}

export function extractFileBlocks(blocks: Array<MessageBlock>): Array<FileMessageBlock> {
  return blocks.filter((b): b is FileMessageBlock => b.type === 'file');
}

export function buildMessageBlocks(prompt: string, fileBlocks: Array<FileMessageBlock>): Array<MessageBlock> {
  const blocks: Array<MessageBlock> = [{type: 'text', text: prompt}];
  return blocks.concat(fileBlocks);
}
